'use client';

import * as SelectPrimitive from '@radix-ui/react-select';
import clsx from 'clsx';

import { ChevronDown, ChevronUp, Close } from '../Icons';

import styles from './TextBlockEditorToolbar.module.scss';

type TextBlockEditorToolbarProps = {
  /**
   * The text to be displayed in the text block.
   */
  text: string;
  /**
   * Determines whether the editor is in edit mode or not.
   */
  editMode: boolean;
  /**
   * The font size of the text block.
   * @default 16
   */
  fontSize?: number;
  /**
   * The font family of the text block.
   */
  fontFamily: string;
  /**
   * The font options to display in the font family dropdown.
   */
  fontOptions: {
    label: string;
    value: string;
    className?: string;
  }[];
  /**
   * Callback function called when the font size is changed.
   * @param fontSize The updated font size value.
   */
  onFontSizeChange: (fontSize: number) => void;
  /**
   * Callback function called when the font family is changed.
   * @param fontFamily The updated font family value.
   */
  onFontFamilyChange: (fontFamily: string) => void;
  /**
   * Callback function called when the close icon is clicked.
   */
  onCloseIconClick: () => void;
  /**
   * Additional class name to apply to the text.
   */
  textClassName?: string;
} & React.HTMLAttributes<HTMLDivElement>;

export const TextBlockEditorToolbar = ({
  text,
  editMode,
  fontSize = 16,
  fontFamily,
  fontOptions,
  // onTextChange,
  onFontSizeChange,
  onFontFamilyChange,
  // onEditIconClick,
  onCloseIconClick,
  className,
  style,
  textClassName,
  ...props
}: TextBlockEditorToolbarProps) => {
  const increaseFontSize = () => onFontSizeChange(fontSize + 1);

  const decreaseFontSize = () => onFontSizeChange(Math.max(fontSize - 1, 1));

  return (
    <div className={styles.toolbar}>
      <div className={styles['toolbar-item']}>
        <SelectPrimitive.Root value={fontFamily} onValueChange={onFontFamilyChange}>
          <SelectPrimitive.Trigger aria-label={fontFamily}>
            <SelectPrimitive.Value />
          </SelectPrimitive.Trigger>

          <SelectPrimitive.Portal>
            <SelectPrimitive.Content
              avoidCollisions={false}
              position="popper"
              side="bottom"
              className={styles['font-content-wrapper']}
            >
              <SelectPrimitive.ScrollUpButton>
                <ChevronUp />
              </SelectPrimitive.ScrollUpButton>

              <SelectPrimitive.Viewport>
                {fontOptions.map(({ label, value, className }, index) => {
                  return (
                    <SelectPrimitive.Item
                      className={clsx(styles['font-option-container'], className)}
                      key={`${value}-${index}`}
                      value={value}
                    >
                      <SelectPrimitive.ItemText>
                        <div>{label}</div>
                      </SelectPrimitive.ItemText>
                    </SelectPrimitive.Item>
                  );
                })}
              </SelectPrimitive.Viewport>

              <SelectPrimitive.ScrollDownButton>
                <ChevronDown />
              </SelectPrimitive.ScrollDownButton>
            </SelectPrimitive.Content>
          </SelectPrimitive.Portal>
        </SelectPrimitive.Root>
      </div>
      <div className={styles['toolbar-item']}>
        <button onClick={decreaseFontSize}>-</button>
        <span className={styles['font-size']}>{fontSize}</span>
        <button onClick={increaseFontSize}>+</button>
      </div>
      <div className={styles.spacer} />
      <button className={styles['close-button']} onClick={onCloseIconClick}>
        <Close size={13} />
      </button>
    </div>
  );
};
